/**
 * Canonical CONDENSED install summary + install-time claim.
 *
 * ANTI_SLOP.md rule 4: every install surface must agree on step count, order,
 * and wording. The full six-step walkthrough is the sacred copy in
 * app/guide/guide-content.ts (guarded by guide-sacred-copy.test.tsx); this
 * module is the single source for the SHORT form that post-download surfaces
 * render (receipt, download toast, sidebar wizard) and for the one honest
 * time estimate used everywhere (nav CTA, HowTo totalTime, page copy).
 *
 * If you reword a step here, every consumer updates together. Do not add a
 * competing hand-written step list to a component.
 */

import { TESLA_FACTS } from "@/lib/tesla-facts";

const { filename, path, folder, formats, portNote } = TESLA_FACTS.usb;
const { sampleRate, bitDepth } = TESLA_FACTS.audio.recommended;

/** The one install-time claim. Nav says "Install in 2 min"; keep in sync. */
export const INSTALL_TIME_LABEL = "2 minutes";
/** ISO-8601 duration for HowTo structured data. */
export const INSTALL_TIME_ISO = "PT2M";

export interface CondensedInstallStep {
  /** Short imperative title (wizard rows, headings). */
  title: string;
  /** Full one-sentence instruction (receipt lines, toast list items). */
  body: string;
}

export const CONDENSED_INSTALL_STEPS: readonly CondensedInstallStep[] = [
  {
    title: "Copy to USB",
    body: `Copy ${filename} onto a FAT32 or exFAT USB drive at ${path}.`,
  },
  {
    title: "Plug into your Tesla",
    body: "Plug the drive into a front USB data port, then open Toybox → Boombox → Lock Sound.",
  },
  {
    title: "Lock the car",
    body: "Pick your sound and lock the car to hear it.",
  },
] as const;

/**
 * Icon vocabulary for the expanded steps. Pages map these keys to their own
 * lucide imports so this module stays icon-library-free.
 */
export type InstallStepIconKey = "file-audio" | "hard-drive" | "usb" | "volume-2";

export interface ExpandedInstallStep {
  /** Icon key for the page-local lucide icon map. */
  icon: InstallStepIconKey;
  /** Short imperative title (card headings). */
  title: string;
  /** Longer instruction for SEO-page install sections. */
  description: string;
}

/**
 * Canonical EXPANDED install steps for SEO pages (horn-sounds, lockchime-wav,
 * tesla-unlock-sound, boombox-sounds). Same facts, longer form. Do not add a
 * competing hand-written step list to a page.
 */
export const EXPANDED_INSTALL_STEPS: readonly ExpandedInstallStep[] = [
  {
    icon: "file-audio",
    title: `Download ${filename}`,
    description: `Pick a sound and download it. Every file arrives named ${filename} in ${sampleRate}, ${bitDepth} WAV, the recommended spec for clean playback. The only hard Tesla requirement is a WAV file named ${filename}.`,
  },
  {
    icon: "hard-drive",
    title: `Copy it into a ${folder} folder`,
    description: `On a ${formats[0]} or ${formats[1]} USB drive, create a folder named ${folder} and place ${filename} inside it. Full path: ${path}.`,
  },
  {
    icon: "usb",
    title: "Plug into a front USB data port",
    description: `Insert the drive into ${portNote}. Give the car a few seconds to index the drive.`,
  },
  {
    icon: "volume-2",
    title: "Switch the source to USB",
    description: "Open Toybox → Boombox → Lock Sound and change from Default to USB. Lock the car to hear it.",
  },
] as const;
